import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  User as UserIcon, Shield, SlidersHorizontal, IdCard, Save, Mail, Phone,
  MapPin, Lock, KeyRound, BellRing, AlertCircle, CheckCircle2,
} from "lucide-react";

const COMING_SOON = ({ children }) => (
  <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
    {children || "Coming Soon"}
  </span>
);

const Section = ({ title, icon: Icon, children, action }) => (
  <div className="border border-border p-5 sm:p-6" data-testid={`profile-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between gap-3 border-b border-border pb-3 mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        <h3 className="font-display text-base sm:text-lg">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, children, required, hint }) => (
  <div className="space-y-1.5">
    <Label className="font-overline text-xs flex items-center gap-1">
      {label}{required && <span className="text-red-600">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

const STATUS_BADGE = (status, ok) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${
    ok ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300"
       : "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"
  }`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
    {status}
  </span>
);

export default function Profile() {
  const { user, reload } = useAuth();
  const { theme, setTheme } = useTheme();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/me/profile");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load profile");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!user) return null;
  if (loading || !data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10" data-testid="profile-loading">
        <div className="animate-pulse h-32 bg-muted/40" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:py-10" data-testid="profile-page">
      <header className="border-b border-border pb-5 sm:pb-6 mb-6 sm:mb-8">
        <div className="font-overline text-accent">Account</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2">My Profile</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage your personal information, security settings, and preferences.
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="rounded-none w-full grid grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="profile" data-testid="profile-tab-profile" className="rounded-none gap-1.5">
            <UserIcon className="w-3.5 h-3.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="profile-tab-security" className="rounded-none gap-1.5">
            <Shield className="w-3.5 h-3.5" /> Security
          </TabsTrigger>
          <TabsTrigger value="preferences" data-testid="profile-tab-preferences" className="rounded-none gap-1.5">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Preferences
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="profile-tab-account" className="rounded-none gap-1.5">
            <IdCard className="w-3.5 h-3.5" /> Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6 space-y-6">
          <ProfileTab data={data} onSaved={(d) => { setData(d); reload(); }}
            theme={theme} setTheme={setTheme} />
        </TabsContent>
        <TabsContent value="security" className="mt-6 space-y-6">
          <SecurityTab data={data} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="preferences" className="mt-6 space-y-6">
          <PreferencesTab data={data} onSaved={(prefs) => setData({ ...data, preferences: prefs })}
            theme={theme} setTheme={setTheme} />
        </TabsContent>
        <TabsContent value="account" className="mt-6 space-y-6">
          <AccountTab data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Tab: PROFILE ----------
function ProfileTab({ data, onSaved }) {
  const initial = useMemo(() => ({
    first_name: data.first_name || (data.name ? data.name.split(" ").slice(0, -1).join(" ") || data.name : ""),
    last_name: data.last_name || (data.name ? data.name.split(" ").slice(-1).join(" ") : ""),
    phone: data.phone || "",
    address: data.address || "",
    city: data.city || "",
    state: data.state || "",
    zip_code: data.zip_code || "",
    country: data.country || "United States",
  }), [data]);
  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const save = async () => {
    if (!form.first_name.trim()) { toast.error("First name is required"); return; }
    if (!form.last_name.trim()) { toast.error("Last name is required"); return; }
    setBusy(true);
    try {
      const r = await api.put("/users/me/profile", form);
      toast.success("Profile updated");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const isDealer = data.role === "dealer";

  return (
    <>
      <Section title="Personal Information" icon={UserIcon}
        action={dirty && (
          <Button data-testid="profile-save-personal" size="sm" onClick={save} disabled={busy}
            className="rounded-none uppercase text-xs tracking-wider">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save Changes"}
          </Button>
        )}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" required>
            <Input data-testid="profile-first-name" className="rounded-none" value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </Field>
          <Field label="Last Name" required>
            <Input data-testid="profile-last-name" className="rounded-none" value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input value={data.email} disabled className="rounded-none opacity-70" />
            <p className="text-[11px] text-muted-foreground">Change email in the Security tab.</p>
          </Field>
          <Field label="Mobile Phone">
            <Input data-testid="profile-phone" className="rounded-none" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-1234" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Street Address">
              <Input data-testid="profile-address" className="rounded-none" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
          </div>
          <Field label="City">
            <Input data-testid="profile-city" className="rounded-none" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </Field>
          <Field label="State / Region">
            <Input data-testid="profile-state" className="rounded-none" value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })} />
          </Field>
          <Field label="ZIP / Postal Code">
            <Input data-testid="profile-zip" className="rounded-none" value={form.zip_code}
              onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
          </Field>
          <Field label="Country">
            <Input data-testid="profile-country" className="rounded-none" value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })} />
          </Field>
        </div>
        {dirty && (
          <div className="mt-5 sm:hidden">
            <Button data-testid="profile-save-personal-mobile" onClick={save} disabled={busy}
              className="w-full rounded-none uppercase text-xs tracking-wider">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        )}
      </Section>

      {isDealer && data.dealer_profile && (
        <Section title="Business Information" icon={IdCard}>
          <p className="text-sm text-muted-foreground mb-4">
            Dealership records are managed from the <a href="/dealer" className="underline">Dealer Console</a>.
            The fields below reflect what we have on file.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <KV k="Business Name" v={data.dealer_profile.business_name} />
            <KV k="Contact Name" v={data.name} />
            <KV k="Business Email" v={data.dealer_profile.email} />
            <KV k="Business Phone" v={data.dealer_profile.phone} />
            <KV k="Dealer License #" v={data.dealer_profile.dealer_license_no} />
            <KV k="EIN / Tax ID" v={data.dealer_profile.ein} />
            <KV k="Address" v={`${data.dealer_profile.business_address || ""} ${data.dealer_profile.city || ""}, ${data.dealer_profile.state || ""} ${data.dealer_profile.zip_code || ""}`.trim()} />
            <KV k="Approval Status" v={
              <span className="uppercase">{STATUS_BADGE(data.dealer_profile.status,
                data.dealer_profile.status === "approved")}</span>
            } />
          </div>
        </Section>
      )}
    </>
  );
}

const KV = ({ k, v }) => (
  <>
    <div className="text-muted-foreground">{k}</div>
    <div className="font-mono-data text-sm break-words">{v || "—"}</div>
  </>
);

// ---------- Tab: SECURITY ----------
function SecurityTab({ data, onChanged }) {
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const changePw = async () => {
    if (pw.next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password",
        { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setPwBusy(false); }
  };

  const changeEmail = async () => {
    if (!emailForm.new_email.includes("@")) { toast.error("Enter a valid email"); return; }
    setEmailBusy(true);
    try {
      const r = await api.post("/users/me/change-email", emailForm);
      toast.success(r.data?.detail || "Confirmation sent");
      setEmailForm({ new_email: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request email change");
    } finally { setEmailBusy(false); }
  };

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setResendBusy(false); }
  };

  return (
    <>
      <Section title="Email Verification" icon={Mail}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm">Current email:</span>
          <span className="font-mono-data text-sm">{data.email}</span>
          {STATUS_BADGE(data.email_verified ? "Verified" : "Unverified", data.email_verified)}
          {!data.email_verified && (
            <Button data-testid="profile-resend-verification" size="sm" variant="outline"
              className="rounded-none ml-auto uppercase text-xs tracking-wider"
              onClick={resendVerification} disabled={resendBusy}>
              {resendBusy ? "Sending…" : "Resend Verification"}
            </Button>
          )}
        </div>
      </Section>

      <Section title="Change Email Address" icon={Mail}>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <Field label="New email">
              <Input data-testid="profile-new-email" type="email" className="rounded-none"
                value={emailForm.new_email}
                onChange={(e) => setEmailForm({ new_email: e.target.value })}
                placeholder="new@example.com" />
            </Field>
          </div>
          <Button data-testid="profile-change-email" onClick={changeEmail} disabled={emailBusy}
            className="rounded-none uppercase text-xs tracking-wider h-10">
            {emailBusy ? "Sending…" : "Send Confirmation"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          We will email a confirmation link to the new address. The change takes effect when you click it.
        </p>
      </Section>

      <Section title="Change Password" icon={KeyRound}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Field label="Current password">
            <Input data-testid="profile-current-pw" type="password" className="rounded-none"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
          </Field>
          <Field label="New password">
            <Input data-testid="profile-new-pw" type="password" className="rounded-none"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          </Field>
          <Field label="Confirm new password">
            <Input data-testid="profile-confirm-pw" type="password" className="rounded-none"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          </Field>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <a href="/forgot-password" data-testid="profile-forgot-pw-link"
            className="text-xs text-muted-foreground underline">
            Forgot your current password?
          </a>
          <Button data-testid="profile-change-pw" onClick={changePw} disabled={pwBusy}
            className="rounded-none uppercase text-xs tracking-wider">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> {pwBusy ? "Saving…" : "Change Password"}
          </Button>
        </div>
      </Section>

      <Section title="Advanced Security" icon={Shield}>
        <ul className="space-y-2.5 text-sm">
          <li data-testid="sec-2fa-row" className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-muted-foreground" />
            Two-Factor Authentication <COMING_SOON />
          </li>
          <li data-testid="sec-sessions-row" className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-muted-foreground" />
            Active Sessions <COMING_SOON />
          </li>
          <li data-testid="sec-history-row" className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-muted-foreground" />
            Login History <COMING_SOON />
          </li>
        </ul>
      </Section>
    </>
  );
}

// ---------- Tab: PREFERENCES ----------
function PreferencesTab({ data, onSaved, theme, setTheme }) {
  const [prefs, setPrefs] = useState(data.preferences);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(prefs) !== JSON.stringify(data.preferences);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/users/me/preferences", prefs);
      onSaved(r.data.preferences);
      if (prefs.theme && prefs.theme !== theme) setTheme(prefs.theme);
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const toggle = (key) => setPrefs({ ...prefs, [key]: !prefs[key] });

  const NOTIFS = [
    { k: "notify_marketing", l: "Marketing emails" },
    { k: "notify_new_notes", l: "New notes posted" },
    { k: "notify_offers", l: "Offers" },
    { k: "notify_counter_offers", l: "Counter offers" },
    { k: "notify_application_updates", l: "Application updates" },
    { k: "notify_dealer_messages", l: "Dealer messages" },
    { k: "notify_system_announcements", l: "System announcements" },
  ];

  return (
    <>
      <Section title="Communication" icon={BellRing}>
        <Field label="Preferred channel">
          <div className="flex gap-2 flex-wrap" role="radiogroup">
            {["email", "sms", "both"].map((opt) => (
              <button key={opt} type="button"
                role="radio" aria-checked={prefs.comm_channel === opt}
                data-testid={`pref-channel-${opt}`}
                onClick={() => setPrefs({ ...prefs, comm_channel: opt })}
                className={`rounded-none uppercase text-xs tracking-wider px-3 h-9 border ${
                  prefs.comm_channel === opt
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border bg-background hover:bg-muted"
                }`}>
                {opt === "sms" ? "SMS" : opt.charAt(0).toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        </Field>
      </Section>

      <Section title="Notifications" icon={BellRing}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NOTIFS.map((n) => (
            <label key={n.k} className="flex items-center gap-2.5 text-sm cursor-pointer">
              <Checkbox data-testid={`pref-${n.k}`} className="rounded-none"
                checked={!!prefs[n.k]} onCheckedChange={() => toggle(n.k)} />
              <span>{n.l}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Theme" icon={SlidersHorizontal}>
        <div className="flex gap-2 flex-wrap" role="radiogroup">
          {["light", "dark", "system"].map((opt) => (
            <button key={opt} type="button"
              role="radio" aria-checked={prefs.theme === opt}
              data-testid={`pref-theme-${opt}`}
              onClick={() => setPrefs({ ...prefs, theme: opt })}
              className={`rounded-none uppercase text-xs tracking-wider px-3 h-9 border ${
                prefs.theme === opt
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border bg-background hover:bg-muted"
              }`}>
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">
          'System' follows your operating system's appearance setting.
        </p>
      </Section>

      <div className="flex justify-end">
        <Button data-testid="prefs-save" onClick={save} disabled={!dirty || busy}
          className="rounded-none uppercase text-xs tracking-wider">
          <Save className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Saving…" : "Save Preferences"}
        </Button>
      </div>
    </>
  );
}

// ---------- Tab: ACCOUNT ----------
function AccountTab({ data }) {
  const fmt = (ts) => { try { return new Date(ts).toLocaleString(); } catch { return ts || "—"; } };
  return (
    <Section title="Account Information" icon={IdCard}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <KV k="Account Type" v={<span className="uppercase font-semibold">{data.role}</span>} />
        <KV k="Member Since" v={fmt(data.created_at)} />
        <KV k="Email Verified" v={STATUS_BADGE(data.email_verified ? "Yes" : "No", !!data.email_verified)} />
        <KV k="Last Login" v={fmt(data.last_login_at)} />
        <KV k="User ID" v={<span className="font-mono-data text-xs">{data.user_id}</span>} />
        <KV k="Email" v={<span className="font-mono-data">{data.email}</span>} />
      </div>
    </Section>
  );
}
